import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Camera,
  ChevronLeft,
  CircleCheck,
  ImageIcon,
  Mic,
  Play,
  Send,
  Square,
  Trash2,
  Video,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import {
  notifyNewChatMessage,
  ensureBrowserNotifications,
  showBrowserNotification,
} from '@/lib/chatNotifications';
import {
  micErrorMessage,
  micUnavailableMessage,
  pickFile,
  releasePreview,
  VoiceRecorder,
  voiceExtensionFor,
  type PickedFile,
} from '@/lib/media';
import { formatDuration, formatTime } from '@/lib/format';
import { useUserId } from '@/components/AuthProvider';
import { useFeedback } from '@/components/Feedback';
import { Avatar, Modal, SoftBadge, Stars } from '@/components/ui';
import Spinner from '@/components/Spinner';
import styles from './ChatRoom.module.css';

type Message = {
  id: string;
  sender_id: string;
  message: string | null;
  voice_url: string | null;
  attachment_url: string | null;
  attachment_type: 'image' | 'video' | null;
  created_at: string;
};

type RoomInfo = {
  bookingId: string | null;
  otherName: string;
  status: string;
  finalPrice: number | null;
};

export default function ChatRoom({ area }: { area: 'client' | 'provider' }) {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const userId = useUserId();
  const { toast } = useFeedback();

  const [room, setRoom] = useState<RoomInfo | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const [attachment, setAttachment] = useState<PickedFile | null>(null);
  const [recording, setRecording] = useState(false);
  const [voiceBlob, setVoiceBlob] = useState<Blob | null>(null);
  const [voiceSeconds, setVoiceSeconds] = useState(0);
  const [sendingVoice, setSendingVoice] = useState(false);

  const recorderRef = useRef<VoiceRecorder | null>(null);
  const timerRef = useRef<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const messageCountRef = useRef(0);

  /* ── Mission workflow state ────────────────────────────────────────────── */
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [priceDraft, setPriceDraft] = useState('');
  const [working, setWorking] = useState(false);

  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewPhoto, setReviewPhoto] = useState<PickedFile | null>(null);
  const [submittingReview, setSubmittingReview] = useState(false);

  const backPath = area === 'client' ? '/app/messages' : '/pro/messages';

  /* ── Load room + messages ──────────────────────────────────────────────── */
  const loadMessages = useCallback(async () => {
    if (!roomId) return;
    const { data } = await supabase
      .from('chat_messages')
      .select('id, sender_id, message, voice_url, attachment_url, attachment_type, created_at')
      .eq('chat_room_id', roomId)
      .order('created_at', { ascending: true });
    if (data) setMessages(data as Message[]);
  }, [roomId]);

  const loadRoom = useCallback(async () => {
    if (!roomId || !userId) return;
    const { data } = await supabase
      .from('chat_rooms')
      .select(
        `id, booking_id, client_id, provider_id,
         bookings(status, final_price),
         client:profiles!chat_rooms_client_id_fkey(id, full_name),
         provider:profiles!chat_rooms_provider_id_fkey(id, full_name)`
      )
      .eq('id', roomId)
      .maybeSingle();

    if (!data) {
      setRoom(null);
      return;
    }

    const row = data as any;
    const viewerIsClient = row.client_id === userId;
    const other = viewerIsClient ? row.provider : row.client;

    setRoom({
      bookingId: row.booking_id ?? null,
      otherName: other?.full_name ?? (viewerIsClient ? 'Prestataire' : 'Client'),
      status: row.bookings?.status ?? '',
      finalPrice: row.bookings?.final_price ?? null,
    });
    setPriceDraft(row.bookings?.final_price ? String(row.bookings.final_price) : '');
  }, [roomId, userId]);

  useEffect(() => {
    if (!roomId || !userId) return;
    let active = true;

    setLoading(true);
    void (async () => {
      await Promise.all([loadRoom(), loadMessages()]);
      if (active) setLoading(false);
    })();

    const channel = supabase
      .channel(`room-${roomId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `chat_room_id=eq.${roomId}`,
        },
        (payload) => {
          const incoming = payload.new as Message;
          if (incoming.sender_id !== userId) {
            showBrowserNotification(
              'Nouveau message',
              incoming.message ?? 'Vous avez reçu un média.'
            );
          }
          void loadMessages();
        }
      )
      .subscribe();

    void ensureBrowserNotifications();

    return () => {
      active = false;
      void channel.unsubscribe();
    };
  }, [roomId, userId, loadRoom, loadMessages]);

  // Keep the transcript pinned to the newest message.
  useEffect(() => {
    if (messages.length === messageCountRef.current) return;
    messageCountRef.current = messages.length;
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages]);

  useEffect(
    () => () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      recorderRef.current?.cancel();
    },
    []
  );

  const closed = room?.status === 'completed';

  /* ── Sending ───────────────────────────────────────────────────────────── */
  const send = async () => {
    const body = text.trim();
    if (closed || (!body && !attachment) || !userId || !roomId || sending) return;

    setSending(true);
    try {
      let attachmentUrl: string | null = null;
      let attachmentType: 'image' | 'video' | null = null;

      if (attachment) {
        const fileName = `chat/${Date.now()}.${attachment.extension}`;
        const { data, error: uploadError } = await supabase.storage
          .from('chat-media')
          .upload(fileName, attachment.file, { contentType: attachment.mimeType });
        if (uploadError || !data) {
          throw new Error(uploadError?.message ?? "Échec de l'upload du média");
        }
        attachmentUrl = supabase.storage.from('chat-media').getPublicUrl(data.path).data.publicUrl;
        attachmentType = attachment.kind;
      }

      const { data: inserted, error } = await supabase
        .from('chat_messages')
        .insert({
          chat_room_id: roomId,
          sender_id: userId,
          message: body || null,
          attachment_url: attachmentUrl,
          attachment_type: attachmentType,
        })
        .select('id')
        .single();

      if (error) throw new Error(error.message);

      void notifyNewChatMessage(inserted.id);

      setText('');
      releasePreview(attachment?.url);
      setAttachment(null);
      await loadMessages();
    } catch (err) {
      toast('error', 'Envoi impossible', err instanceof Error ? err.message : undefined);
    } finally {
      setSending(false);
    }
  };

  const attachMedia = async (kind: 'image' | 'video') => {
    if (sending || sendingVoice) return;
    const picked = await pickFile(kind);
    if (picked) {
      releasePreview(attachment?.url);
      setAttachment(picked);
    }
  };

  /* ── Voice notes ───────────────────────────────────────────────────────── */
  const startRecording = async () => {
    const reason = VoiceRecorder.unavailableReason();
    if (reason) {
      toast('error', 'Micro indisponible', micUnavailableMessage(reason));
      return;
    }
    try {
      const recorder = new VoiceRecorder();
      await recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
      setVoiceSeconds(0);
      timerRef.current = window.setInterval(() => setVoiceSeconds((value) => value + 1), 1000);
    } catch (err) {
      toast('error', 'Micro indisponible', micErrorMessage(err));
    }
  };

  const stopRecording = async () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    try {
      const blob = await recorderRef.current?.stop();
      if (blob) setVoiceBlob(blob);
    } catch {
      toast('error', 'Enregistrement interrompu');
    } finally {
      recorderRef.current = null;
      setRecording(false);
    }
  };

  const discardVoice = () => {
    setVoiceBlob(null);
    setVoiceSeconds(0);
  };

  const sendVoice = async () => {
    if (!voiceBlob || !userId || !roomId) return;
    setSendingVoice(true);
    try {
      const contentType = voiceBlob.type || 'audio/webm';
      const fileName = `chat/${Date.now()}.${voiceExtensionFor(contentType)}`;
      const { data, error: uploadError } = await supabase.storage
        .from('voice-messages')
        .upload(fileName, voiceBlob, { contentType });
      if (uploadError || !data) throw new Error(uploadError?.message ?? "Échec de l'upload vocal");

      const voiceUrl = supabase.storage.from('voice-messages').getPublicUrl(data.path).data
        .publicUrl;

      const { data: inserted, error } = await supabase
        .from('chat_messages')
        .insert({ chat_room_id: roomId, sender_id: userId, voice_url: voiceUrl })
        .select('id')
        .single();
      if (error) throw new Error(error.message);

      void notifyNewChatMessage(inserted.id);
      discardVoice();
      await loadMessages();
    } catch (err) {
      toast('error', 'Envoi vocal impossible', err instanceof Error ? err.message : undefined);
    } finally {
      setSendingVoice(false);
    }
  };

  /* ── Mission workflow ──────────────────────────────────────────────────── */
  const updateBooking = async (patch: Record<string, unknown>, nextStatus: string) => {
    if (!room?.bookingId) return;
    setWorking(true);
    try {
      const { error } = await supabase.from('bookings').update(patch).eq('id', room.bookingId);
      if (error) throw new Error(error.message);
      setRoom((current) => (current ? { ...current, status: nextStatus, ...(patch.final_price ? { finalPrice: Number(patch.final_price) } : {}) } : current));
    } catch (err) {
      toast('error', 'Action impossible', err instanceof Error ? err.message : undefined);
    } finally {
      setWorking(false);
    }
  };

  // Client confirms (and may adjust) the price, handing the mission to the provider.
  const confirmPrice = async () => {
    const price = Number.parseFloat(priceDraft);
    if (!Number.isFinite(price) || price <= 0) {
      toast('error', 'Prix invalide', 'Saisissez un montant supérieur à zéro.');
      return;
    }
    await updateBooking({ status: 'accepted', final_price: price }, 'accepted');
    setPriceModalOpen(false);
    toast('success', 'Prix confirmé', 'Le prestataire peut maintenant démarrer la mission.');
  };

  const submitReview = async () => {
    if (!room?.bookingId || reviewRating === 0) return;
    setSubmittingReview(true);
    try {
      let photoUrl: string | null = null;
      if (reviewPhoto) {
        const fileName = `completions/${Date.now()}.${reviewPhoto.extension}`;
        const { data, error: uploadError } = await supabase.storage
          .from('completion-photos')
          .upload(fileName, reviewPhoto.file, { contentType: reviewPhoto.mimeType });
        if (!uploadError && data) {
          photoUrl = supabase.storage.from('completion-photos').getPublicUrl(data.path).data
            .publicUrl;
        }
      }

      const { error } = await supabase
        .from('bookings')
        .update({
          status: 'completed',
          rating: reviewRating,
          review_comment: reviewComment.trim() || null,
          completion_photo: photoUrl,
        })
        .eq('id', room.bookingId);
      if (error) throw new Error(error.message);

      setRoom((current) => (current ? { ...current, status: 'completed' } : current));
      toast('success', 'Merci pour votre avis !');
    } catch (err) {
      toast('error', "Impossible d'envoyer l'avis", err instanceof Error ? err.message : undefined);
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) return <Spinner block label="Chargement de la conversation..." />;

  if (!room) {
    return (
      <div className={styles.missing}>
        <p>Conversation introuvable.</p>
        <button type="button" className="btn btn--outline" onClick={() => navigate(backPath)}>
          Retour aux messages
        </button>
      </div>
    );
  }

  return (
    <div className={styles.room}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backBtn}
          onClick={() => navigate(backPath)}
          aria-label="Retour aux conversations"
        >
          <ChevronLeft size={22} />
        </button>
        <Avatar name={room.otherName} size={40} tone="orange" />
        <div className={styles.headerText}>
          <p className={styles.headerName}>{room.otherName}</p>
          {room.status ? <SoftBadge status={room.status} /> : null}
        </div>

        {area === 'client' && room.status === 'assigned' ? (
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => setPriceModalOpen(true)}
          >
            <Play size={14} /> Démarrer
          </button>
        ) : null}
      </header>

      <div className={styles.transcript} ref={scrollRef}>
        {messages.length === 0 ? (
          <p className={styles.emptyTranscript}>Commencez la conversation !</p>
        ) : null}

        {messages.map((message) => {
          const mine = message.sender_id === userId;
          return (
            <div
              key={message.id}
              className={`${styles.messageRow} ${mine ? styles.messageRowMine : ''}`}
            >
              <div className={`${styles.bubble} ${mine ? styles.bubbleMine : styles.bubbleOther}`}>
                {message.attachment_url && message.attachment_type === 'image' ? (
                  <a href={message.attachment_url} target="_blank" rel="noreferrer">
                    <img className={styles.messageImage} src={message.attachment_url} alt="" />
                  </a>
                ) : null}

                {message.attachment_url && message.attachment_type === 'video' ? (
                  <video className={styles.messageVideo} controls src={message.attachment_url} />
                ) : null}

                {message.voice_url ? (
                  <audio className={styles.messageAudio} controls src={message.voice_url} />
                ) : null}

                {message.message ? <p className={styles.bubbleText}>{message.message}</p> : null}

                <span className={styles.bubbleTime}>{formatTime(message.created_at)}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Provider: accept or refuse the client's price ── */}
      {area === 'provider' && room.status === 'accepted' && room.finalPrice !== null ? (
        <div className={styles.actionCard}>
          <div>
            <p className={styles.actionLabel}>Prix proposé par le client</p>
            <p className={styles.actionAmount}>{room.finalPrice} MAD</p>
          </div>
          <div className={styles.actionButtons}>
            <button
              type="button"
              className="btn btn--danger btn--sm"
              onClick={() => void updateBooking({ status: 'assigned' }, 'assigned')}
              disabled={working}
            >
              Refuser
            </button>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => void updateBooking({ status: 'in_progress' }, 'in_progress')}
              disabled={working}
            >
              Accepter
            </button>
          </div>
        </div>
      ) : null}

      {/* ── Provider: mark the mission finished ── */}
      {area === 'provider' && room.status === 'in_progress' ? (
        <div className={styles.actionCard}>
          <div className={styles.actionRow}>
            <CircleCheck size={20} color="var(--green-deep)" />
            <p className={styles.actionLabel}>Mission terminée ?</p>
          </div>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => void updateBooking({ status: 'pending_review' }, 'pending_review')}
            disabled={working}
          >
            Terminer la mission
          </button>
        </div>
      ) : null}

      {/* ── Client: rate the completed mission ── */}
      {area === 'client' && room.status === 'pending_review' ? (
        <div className={styles.reviewCard}>
          <p className={styles.reviewTitle}>Évaluer la mission</p>
          <div className={styles.reviewStars}>
            <Stars value={reviewRating} size={30} onChange={setReviewRating} />
          </div>
          <textarea
            className="textarea"
            style={{ minHeight: 70 }}
            placeholder="Votre commentaire (optionnel)..."
            maxLength={300}
            value={reviewComment}
            onChange={(event) => setReviewComment(event.target.value)}
          />
          <button
            type="button"
            className={styles.reviewPhotoBtn}
            onClick={async () => {
              const picked = await pickFile('image', 'camera');
              if (picked) {
                releasePreview(reviewPhoto?.url);
                setReviewPhoto(picked);
              }
            }}
          >
            {reviewPhoto ? (
              <img className={styles.reviewPhoto} src={reviewPhoto.url} alt="Aperçu du résultat" />
            ) : (
              <span className={styles.reviewPhotoInner}>
                <Camera size={18} /> Ajouter une photo du résultat
              </span>
            )}
          </button>
          <button
            type="button"
            className="btn btn--primary btn--block"
            onClick={() => void submitReview()}
            disabled={reviewRating === 0 || submittingReview}
          >
            {submittingReview ? <Spinner size={16} color="var(--ink)" /> : "Envoyer l'avis"}
          </button>
        </div>
      ) : null}

      {/* ── Composer ── */}
      {closed ? (
        <p className={styles.closedBanner}>Mission terminée — la conversation est fermée.</p>
      ) : recording ? (
        <div className={styles.composer}>
          <span className={styles.recordingPill}>
            <span className={styles.recordingDot} />
            Enregistrement {formatDuration(voiceSeconds)}
          </span>
          <button
            type="button"
            className={styles.stopBtn}
            onClick={() => void stopRecording()}
            aria-label="Arrêter l'enregistrement"
          >
            <Square size={18} fill="#fff" />
          </button>
        </div>
      ) : voiceBlob ? (
        <div className={styles.composer}>
          <span className={styles.voicePill}>
            <Mic size={17} color="var(--orange)" />
            Vocal · {formatDuration(voiceSeconds)}
          </span>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={discardVoice}
            aria-label="Supprimer le vocal"
          >
            <Trash2 size={17} />
          </button>
          <button
            type="button"
            className={styles.sendBtn}
            onClick={() => void sendVoice()}
            disabled={sendingVoice}
            aria-label="Envoyer le vocal"
          >
            {sendingVoice ? <Spinner size={16} color="var(--ink)" /> : <Send size={18} />}
          </button>
        </div>
      ) : (
        <div>
          {attachment ? (
            <div className={styles.attachmentPreview}>
              {attachment.kind === 'image' ? (
                <img className={styles.attachmentImage} src={attachment.url} alt="" />
              ) : (
                <video className={styles.attachmentImage} controls src={attachment.url} />
              )}
              <div className={styles.attachmentBar}>
                <span className={styles.attachmentLabel}>
                  {attachment.kind === 'image' ? 'Image prête à envoyer' : 'Vidéo prête à envoyer'}
                </span>
                <button
                  type="button"
                  className={styles.iconBtn}
                  onClick={() => {
                    releasePreview(attachment.url);
                    setAttachment(null);
                  }}
                  aria-label="Retirer la pièce jointe"
                >
                  <Trash2 size={17} />
                </button>
              </div>
            </div>
          ) : null}

          <div className={styles.composer}>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={() => void startRecording()}
              aria-label="Enregistrer un message vocal"
            >
              <Mic size={19} />
            </button>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={() => void attachMedia('image')}
              aria-label="Joindre une image"
            >
              <ImageIcon size={19} />
            </button>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={() => void attachMedia('video')}
              aria-label="Joindre une vidéo"
            >
              <Video size={19} />
            </button>

            <textarea
              className={styles.input}
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  void send();
                }
              }}
              placeholder="Écrire un message..."
              maxLength={500}
              rows={1}
              aria-label="Message"
            />

            <button
              type="button"
              className={styles.sendBtn}
              onClick={() => void send()}
              disabled={sending || (!text.trim() && !attachment)}
              aria-label="Envoyer"
            >
              {sending ? <Spinner size={16} color="var(--ink)" /> : <Send size={18} />}
            </button>
          </div>
        </div>
      )}

      <Modal
        open={priceModalOpen}
        onClose={() => setPriceModalOpen(false)}
        title="Confirmer ou modifier le prix"
      >
        <p className={styles.modalText}>
          Vous pouvez ajuster le prix proposé avant de confirmer avec {room.otherName}.
        </p>
        <div className={styles.priceRow}>
          <input
            className={styles.priceInput}
            type="number"
            min="0"
            step="1"
            value={priceDraft}
            onChange={(event) => setPriceDraft(event.target.value)}
            placeholder="Prix"
            autoFocus
            aria-label="Prix en MAD"
          />
          <span className={styles.priceSuffix}>MAD</span>
        </div>
        <div className={styles.modalActions}>
          <button
            type="button"
            className="btn btn--outline"
            onClick={() => setPriceModalOpen(false)}
          >
            Annuler
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void confirmPrice()}
            disabled={working || !priceDraft.trim()}
          >
            {working ? <Spinner size={16} color="var(--ink)" /> : 'Confirmer'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
